<template>
    <loading v-if="loading.order"></loading>
    <div v-else>

        <div class="columns is-vcentered heading-con">

            <div class="column is-narrow">
                <router-link :to="{ name : $ROUTES.ORDERS_INDEX }" class="button"> <i class="fa fa-arrow-left m-r-md"></i> Back</router-link>
            </div>

            <div class="column">
                <h2 class="title is-size-3 has-text-weight-light"># {{order.id}}</h2>
            </div>

            <div class="column is-narrow">
                <div class="flex-con sm-pad-lr">
                     <div>
                        <button
                            v-if="authorizeAction.canToggleAuth"
                            :disabled="!orderIsOpen"
                            class="button is-danger"
                            @click="confirmCancelModal.active = true"
                        >
                            Cancel
                        </button>
                     </div>
                    <div v-if="$getSessionContext().paymentCollection">
                        <button
                            v-if="authorizeAction.canToggleAuth"
                            @click="authorizeOrder"
                            :disabled="!orderIsOpen"
                            class="button"
                            :class="[authorizeAction.class, { 'is-loading' : loading.authorize }]"
                        >
                            {{authorizeAction.label}}
                        </button>
                        <span v-else>
                            {{paymentDateLabel}}
                            <timestamp :value="order.payment.paidAt" :is-full="true"></timestamp>
                        </span>
                    </div>
                </div>
            </div>

        </div>

        <div class="order-view-content">

            <div class="columns">

                <div class="column is-one-fifth meta-data-column">

                    <div class="flex-con is-grow meta-data-title">
                        <div>
                            <span class="tag is-medium" :class="'order-status-bg-color-' + order.orderStatusid">{{orderStatus.name}}</span>
                        </div>
                        <div class="is-narrow progress-con">
                            <all-order-status-icons :order="order"></all-order-status-icons>
                        </div>
                    </div>

                    <div class="meta-data-group">
                        <div class="datum">
                            <div>Order #</div>
                            <div>{{order.id}}</div>
                        </div>
                        <div class="datum">
                            <div>Reference #</div>
                            <div>{{order.referenceNumber}}</div>
                        </div>
                        <div class="datum">
                            <div>Order Date</div>
                            <div><timestamp :value="order.orderedAt" :is-full="true"></timestamp></div>
                        </div>
                        <div class="datum">
                            <div>Shipping Method</div>
                            <div
                                v-if="!order.channelShippingMethod"
                            >
                                {{order.shippingText}}
                            </div>
                            <div
                                v-else
                            >
                                {{order.channelShippingMethod.name}}
                            </div>
                        </div>
                        <div class="datum">
                            <div>Invoice #</div>
                            <div>{{order.payment.id}}</div>
                        </div>
                        <div v-if="order.canceledAt" class="datum">
                            <div><span class="order-status-color-3">Canceled</span></div>
                            <div><timestamp class="order-status-color-3" :value="order.canceledAt" :is-full="true"></timestamp></div>
                        </div>
                    </div>
                    <div class="meta-data-group">
                        <editable-address
                            class="datum"
                            :update-method="updateBillingAddress"
                            :disabled="!orderIsOpen"
                            editable
                            :address="order.billingAddress"
                            show-no-address-info
                            @updated="order.billingAddress = $event"
                        >
                            <template #default>
                                <div class="has-text-grey is-uppercase">Billing Address</div>
                            </template>
                        </editable-address>
                    </div>

                </div>

                <div class="column">

                    <table class="table is-fullwidth line-items" aria-label="Line Items">
                        <thead>
                            <tr>
                                <th class="is-skinny-no-wrap" scope="col">Shipping Address</th>
                                <th class="is-skinny" scope="col">Image</th>
                                <th class="is-skinny" scope="col">SKU</th>
                                <th class="is-skinny" scope="col">Title</th>
                                <th class="has-text-right is-skinny" scope="col">Weight</th>
                                <th class="has-text-right is-skinny" scope="col">Shipped</th>
                                <th class="is-skinny" scope="col">Tracking</th>
                                <th class="has-text-right is-skinny" scope="col">Qty</th>
                                <th class="has-text-right is-skinny" scope="col">Price</th>
                                <th class="has-text-right is-skinny" scope="col">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody v-for="(shippingAddressItemGroup, i) in Object.values(orderItemsGroupedByShippingAddress)" :key="i">
                            <tr v-for="item in shippingAddressItemGroup" :key="item.id">
                                <td>
                                    <div v-if="shippingAddressItemGroup[0].id === item.id">
                                        <editable-address
                                            v-if="item.shippingAddress ? item.shippingAddress : order.shippingAddress"
                                            class="box"
                                            :disabled="!orderIsOpen"
                                            editable
                                            :address="item.shippingAddress ? item.shippingAddress : order.shippingAddress"
                                            :show-no-address-info="!item.shippingAddress"
                                            @updated="updateItemShippingAddress($event, item)"
                                        />
                                    </div>
                                </td>
                                <td class="img-col"><img class="image" :src="$srcOverwrite(lineItemData(item, 'image', 'imageUrl'))"/> </td>
                                <td class="is-skinny">{{item.sku}}</td>
                                <td v-html="lineItemData(item, 'title')"></td>
                                <td class="has-text-right is-skinny">{{item.weight}}<small class="has-text-grey-dark is-size-7">lbs</small></td>
                                <td class="has-text-right is-skinny">
                                    {{item.shippedQuantity}} / {{item.quantity}}
                                    <i class="fa fa-truck shipped-icon" :class="shippedClass(item)"></i>
                                </td>
                                <td>
                                    <span v-show="!order.shipments.length">N/A</span>
                                    <span v-for="shipment in order.shipments" :key="shipment.id">
                                        {{shipment.trackingNumber}}
                                    </span>
                                </td>
                                <td class="has-text-right is-skinny"><number :value="item.quantity"></number></td>
                                <td class="has-text-right is-skinny"><money :value="item.price"></money></td>
                                <td class="has-text-right is-skinny"><money :value="item.price * item.quantity"></money></td>
                            </tr>
                            <tr>
                                <td :colspan="10">
                                    <hr />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="columns">
                        <div class="column">
                            <div v-show="order.note" class="order-note">
                                <h6 class="title is-size-6 is-uppercase has-text-weight-light">Order Note</h6>
                                <div class="note" v-html="order.note"/>
                            </div>
                        </div>
                        <div class="column invoice-summary-con">
                            <table>
                                <tbody>
                                    <tr>
                                        <td>Subtotal</td>
                                        <td><money :value="order.payment.subtotal"></money></td>
                                    </tr>
                                    <tr v-for="item in paymentLineItems" :key="item.name">
                                        <td>{{item.name}}</td>
                                        <td><money :value="item.cost"></money></td>
                                    </tr>
                                    <tr class="total-row">
                                        <td>Total</td>
                                        <td><money :value="order.payment.total"></money></td>
                                    </tr>
                                    <tr
                                        v-if="order.payment.creditAppliedTowardsTotal"
                                        class="credit-applied-row"
                                    >
                                        <td>Credit Applied</td>
                                        <td>(<money :value="order.payment.creditAppliedTowardsTotal"></money>)</td>
                                    </tr>
                                    <tr
                                        v-if="order.payment.creditAppliedTowardsTotal"
                                        class="paid-row"
                                    >
                                        <td>Paid</td>
                                        <td><money :value="order.payment.total - order.payment.creditAppliedTowardsTotal"></money></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div v-if="order.payment.refundedAmount" class="message is-warning">
                        <div class="message-body">
                            This order was
                            <b>{{order.payment.refundedAmount >= order.payment.total ? "fully" : "partially" }}
                            refunded</b> to your account in the amount of <money :value="order.payment.refundedAmount"></money>
                            on <timestamp :value="order.payment.refundedLastAt" :is-full="true"></timestamp>
                        </div>
                    </div>

                    <div v-if="order.payment.creditedAmount" class="message is-warning">
                        <div class="message-body">
                            This order was
                            <b>{{order.payment.creditedAmount >= order.payment.total ? "fully" : "partially" }}
                            credited</b> to your account in the amount of <money :value="order.payment.creditedAmount"></money>
                            on <timestamp :value="order.payment.creditedLastAt" :is-full="true"></timestamp>
                        </div>
                    </div>

                    <div class="shipments-con">
                        <h2 class="title is-size-5 has-text-weight-light">Shipments</h2>
                        <div v-if="!order.shipments.length" class="message is-warning">
                            <div class="message-body">
                                No shipment/tracking data is available for this order yet
                            </div>
                        </div>
                        <table class="table" v-else>
                            <thead>
                                <tr>
                                    <th scope="col">Tracking #</th>
                                    <th scope="col">Items</th>
                                    <th scope="col">Shipped At</th>
                                    <th scope="col">Carrier</th>
                                    <th scope="col">Method</th>
                                    <th scope="col">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="shipment in order.shipments" :key="shipment.id">
                                    <td>{{shipment.trackingNumber}}</td>
                                    <td>
                                        <div v-for="item in shipment.orderItems" :key="item.id" class="flex-con is-grow shipment-item">
                                            <div>
                                                {{item.sku}}
                                            </div>
                                            <div>
                                                <span class="tag is-light tooltip is-tooltip-right" :class="shipmentQuantityClass(item)" data-tooltip="Shipped Quantity">
                                                    {{item.quantity}}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td><timestamp :value="shipment.shippedAt"></timestamp></td>
                                    <td>{{shipment.carrier}}</td>
                                    <td>{{shipment.method}}</td>
                                    <td><money :value="shipment.cost"></money></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

        </div>



        <confirm-modal
            :active="confirmCancelModal.active"
            :close="() => { confirmCancelModal.active = false }"
            :callback="cancelOrder"
            :loading="confirmCancelModal.loading"
        >
            <h2 class="is-size-4 has-text-centered">Are you sure you want to cancel this order?</h2>
        </confirm-modal>

    </div>
</template>

<script>
import StatusUtil from '../../util/StatusUtil';

import Orders from '../../api/Orders';
import _ from 'underscore';

import AllOrderStatusIcons from "../../components/order-progress/AllOrderStatusIcons";
import EditableAddress from "@/components/address/EditableAddress.vue";

export default {
    name : "OrdersView",

    components : {
        AllOrderStatusIcons,
        EditableAddress,
    },

    data () {
        return {
            active_tab : "overview",
            order : {
                payment : {},
            },
            loading : {
                order : true,
                authorize : false,
            },
            confirmCancelModal : {
                active : false,
                loading : false,
            },
        }
    },

    beforeMount () {
        this.fetchOrder();
    },

    computed : {

        orderStatus () {
            return StatusUtil.ORDER.findById(this.order.orderStatusId);
        },

        paymentDateLabel () {
            return this.order.paymentStatusId === StatusUtil.PAYMENT.AWAITING_CLEARANCE.id
                || this.order.paymentStatusId === StatusUtil.PAYMENT.DECLINED.id
                ? 'Payment Initiated On'
                : 'Paid On';
        },

        authorizeAction () {
            return {
                canToggleAuth : this.order.paymentStatusId === StatusUtil.PAYMENT.AUTHORIZED.id || this.order.paymentStatusId === StatusUtil.PAYMENT.UNAUTHORIZED.id,
                disabled : this.order.paymentStatusId === StatusUtil.PAYMENT.PAID.id,
                class : this.order.paymentStatusId === StatusUtil.PAYMENT.UNAUTHORIZED.id ? "is-primary" : "is-warning",
                label : this.order.paymentStatusId === StatusUtil.PAYMENT.AUTHORIZED.id? "Revoke Authorization" : "Authorize For Payment"
            }
        },

        orderIsOpen () {
            return this.order.orderStatusId === StatusUtil.ORDER.OPEN.id;
        },

        paymentLineItems () {
            const items = [];
            if(this.order.payment.shippingCost !== null) {
                items.push({
                    name : this.order.payment.shippingCost !== null ? this.order.payment.shippingTitle || 'Shipping Cost' : 'N/A',
                    cost : this.order.payment.shippingCost,
                });
            }
            if(this.order.payment.dropshipFee !== null) {
                items.push({
                    name : this.order.payment.dropshipFee !== null ? this.order.payment.dropshipFeeTitle || 'Dropship Fee' : 'N/A',
                    cost : this.order.payment.dropshipFee,
                });
            }
            if(this.order.payment.taxCost !== null) {
                items.push({
                    name : this.order.payment.taxCost !== null ? this.order.payment.taxTitle || 'Tax' : 'N/A',
                    cost : this.order.payment.taxCost,
                });
            }
            if(this.order.payment.otherCost !== null) {
                items.push({
                    name : this.order.payment.otherCost !== null ? this.order.payment.otherTitle || 'Other' : 'N/A',
                    cost : this.order.payment.otherCost,
                });
            }
            return items;
        },
        orderItemsGroupedByShippingAddress() {
            const itemsSortedById = _.sortBy(this.order.orderItems, 'id');
            return _.groupBy(itemsSortedById, 'shippingAddressId');
        },
    },

    methods : {

        fetchOrder () {
            Orders.get(this.$route.params.id).then(response => {
                if(!response.data.id) {
                    this.$router.push({ name : 'orders.index'});
                    return;
                }
                this.order = response.data;
                document.title = `Order #${this.order.id}`;
                this.loading.order = false;
            });
        },

        lineItemData (item, key, variantKey) {
            if(item[key]) {
                return item[key];
            }
            variantKey = variantKey || key;
            if(item.virtualVariant && item.virtualVariant[variantKey]) {
                return item.virtualVariant[variantKey];
            }
            return null;
        },

        authorizeOrder () {

            this.loading.authorize = true;
            const newStatus = this.order.paymentStatusId === StatusUtil.PAYMENT.AUTHORIZED.id ? StatusUtil.PAYMENT.UNAUTHORIZED : StatusUtil.PAYMENT.AUTHORIZED;
            Orders.authorize(this.order.id, newStatus.id).then(() => {
                this.$notify({
                    text : `Order has been ${newStatus.name}`,
                });
            }).finally(() => {
                this.loading.authorize = false;
                this.fetchOrder();
            });
        },

        shippedClass (item) {
            if(!item.shippedQuantity){
                return 'status-color-1';
            }
            if(item.shippedQuantity === item.quantity){
                return 'status-color-3';
            }
            return 'status-color-2';
        },

        shipmentQuantityClass (shipmentItem) {
            const item = _.findWhere(this.order.orderItems, { sku : shipmentItem.sku });
            if(item && item.quantity !== shipmentItem.quantity){
                return 'is-warning';
            }
            return 'is-light';
        },

        updateBillingAddress(address) {
            return Orders.updateBillingAddress(this.order.id, address).then(response => {
                this.order.billingAddress = response.data;
                this.order.billingAddressId = response.data.id;
            });
        },

        async updateItemShippingAddress(address, item) {
            this.loading.order = true;
            // if this item already had a shipping address, check for updating other items grouped with it; otherwise just update this item
            const itemsToUpdate = item.shippingAddressId
                ? this.orderItemsGroupedByShippingAddress[item.shippingAddressId]
                : item;
            try {
                for (let item of itemsToUpdate) {
                    const response = await Orders.updateItemShippingAddress(this.order.id, item.id, address);
                    this.$set(item, 'shippingAddress', response.data);
                    this.$set(item, 'shippingAddressId', response.data.id);
                }
                this.$notifySuccess("Address updated");
            } catch (error) {
                this.$notifyError('An error occurred while updating item shipping address');
            } finally {
                this.loading.order = false;
            }
        },

        cancelOrder (confirm) {
            if(!confirm){
                this.confirmCancelModal.active = false;
                return;
            }
            this.confirmCancelModal.loading = true;
            if(!this.orderIsOpen){
                this.$notify({ text : 'Can only cancel orders that are open', type : 'danger'});
                this.confirmCancelModal.loading = false;
                this.confirmCancelModal.active = false;
                return;
            }
            Orders.cancel(this.order.id).then(() => {
                this.$notify({ text : 'Canceled order successfully', type : 'success' });
                this.fetchOrder();
            }).finally(() => {
                this.confirmCancelModal.loading = false;
                this.confirmCancelModal.active = false;
            });
        },

    }

}
</script>

<style scoped lang="scss">
    @import "../../assets/css/_theme";

    .heading-con {
        margin-bottom:0 !important;
    }

    .order-view-content {
        border-top:1px solid $grey-lighter;
        padding-top:12px;
    }

    .progress-con {
        /deep/ .fa {
            font-size:18px;
        }
    }

    .meta-data-column {
        border-right:1px solid $grey-lighter;
        padding-right:20px;
        margin-right:20px;

        & > .meta-data-title {
            padding-bottom:20px;
        }

        & > .meta-data-group {
            margin-bottom:10px;
            padding-bottom:10px;
            border-bottom:1px solid $grey-lighter;

            &:last-child {
                margin-bottom:0;
                padding-bottom:0;
                border-bottom:none;
            }

            & > .datum {
                margin-bottom:5px;
                padding-bottom:5px;
                border-bottom:1px solid $white-ter;

                &:last-child {
                    margin-bottom:0;
                    padding-bottom:0;
                    border-bottom:none;
                }


                & > div {
                    &:first-child {
                        @include headingFont;
                        color:$grey;
                        text-transform:uppercase;
                    }
                    &:last-child {
                        font-size:14px;
                        text-align:right;
                    }

                    &.address {
                        text-align:left;
                        font-size:13px;
                    }

                }
            }
        }

    }

    table.line-items {
        font-size:13px;
        tr > th {
            background-color:$white-ter;
            font-size:14px;
            border-width:0;
        }
        td {
            border-bottom:none;
        }
        img {
            max-height:40px;
            height:auto;
            width:auto;
        }

        .img-col {
            width:32px;
            img {
                max-width:32px;
                width:auto;
                height:auto;
            }
        }

        .shipped-icon {
            font-size:10px;
            transform:translate(0, -1px);
        }
    }

    .order-note {
        border-left:2px solid $info;
        padding:5px 0 5px 20px;
        .title {
            margin:0 0 10px 0;
        }
        .note {
            font-size:14px;
        }
    }

    .invoice-summary-con {
        table {
            float:right;
            tr > td {
                border-bottom:1px solid $white-ter;
                padding:4px .75em;
                &:first-child {
                    font-weight:bold;
                    padding-right:120px;
                    font-size:13px;
                }
                &:last-child {
                    text-align:right;
                }
            }

            .total-row {
                background-color:$white-ter;
                td {
                    font-size:18px !important;
                    padding-top:8px;
                    padding-bottom:8px;
                }
            }
            .credit-applied-row {
                background-color:#fffbcc;
            }
            .paid-row {
                background-color:$white-bis;
                td {
                    font-size:16px !important;
                    padding-top:6px;
                    padding-bottom:6px;
                }
            }
        }
    }

    .shipments-con {
        border-top:1px solid $grey-lighter;
        padding-top:20px;
        table {
            font-size:13px;
            min-width:72.5%;

            tr > th {
                background-color:$white-ter;
                font-size:14px;
                border-width:0;
            }

        }
        .shipment-item {
            .tag {
                padding:6px 10px;
                margin-left:10px;
            }
        }
    }

</style>
